/**
 * StudentPicker
 *
 * Lightweight controlled-input + result-list picker that surfaces
 * existing RosterPersons (students) so the parent can attach one to
 * an active reunification event via `useCreateEventStudent`. Mirrors
 * `StaffPicker` one-to-one in shape — same debounced
 * search, capped page size, exclude-already-on-event filter — but
 * targets the planning-students endpoint because student identity
 * lives in the roster (not the auth-user set; see ADR-064 §LAYER 3).
 *
 * Scope (v1): existing roster students only. Ad-hoc walk-ins
 * (creating a new RosterPerson on the fly during an event) are
 * explicitly out of scope — they require an identity-verification
 * policy.
 *
 * Compliance: Restricted-Student-Record-PII tier. The picker
 * displays first/last name + grade + external_reference_id; that's
 * the same minimum disclosure already in `StudentsList.tsx` for
 * the pre-event planning view, so this isn't introducing a new
 * exposure surface. FERPA logging happens server-side when the
 * actual POST `/events/{event_public_id}/students/` lands —
 * the picker itself is read-only against the planning endpoint.
 */

import { useState, useMemo } from 'react';
import {
  Box,
  Center,
  Input,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { CardButton } from '@themes/aegix';
import { usePlanningStudents } from '../../hooks/queries/useReunification';
import type { PlanningStudent } from '../../types/ReunificationTypes';

const PICKER_PAGE_SIZE = 20;

export interface StudentPickerSelection {
  rosterPersonId: number;
  publicId: string;
  name: string;
  grade: string | null;
  externalReferenceId: string;
}

export interface StudentPickerProps {
  /** RosterPerson ids already on the event — filtered out of results. */
  excludeRosterPersonIds?: number[];
  /** The currently picked student (controlled). */
  selected: StudentPickerSelection | null;
  /** Called when the operator clicks a row. */
  onSelect: (selection: StudentPickerSelection) => void;
  /** Optional placeholder for the search input. */
  placeholder?: string;
  /** Optional fixed height for the result list (default 240). */
  listHeight?: number;
  /** Optional data-e2e prefix to keep parents' selectors stable. */
  dataE2ePrefix?: string;
}

export function StudentPicker({
  excludeRosterPersonIds,
  selected,
  onSelect,
  placeholder = 'Search students by name or ID…',
  listHeight = 240,
  dataE2ePrefix = 'student-picker',
}: StudentPickerProps) {
  const [search, setSearch] = useState('');

  const { data, isLoading, isFetching, error } = usePlanningStudents({
    search: search || undefined,
    page_size: PICKER_PAGE_SIZE,
    ordering: 'last_name',
  });

  const excludeSet = useMemo(
    () => new Set(excludeRosterPersonIds ?? []),
    [excludeRosterPersonIds],
  );

  const candidates = useMemo<PlanningStudent[]>(() => {
    const all = data?.results ?? [];
    return all.filter((s) => {
      if (!Number.isFinite(s.id) || s.id <= 0) return false;
      return !excludeSet.has(s.id);
    });
  }, [data?.results, excludeSet]);

  return (
    <VStack align="stretch" gap={3}>
      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder.replace(/[….]+$/, '')}
        size="sm"
        data-e2e={`${dataE2ePrefix}-search`}
      />

      <Box
        borderWidth="1px"
        borderColor="border.subtle"
        borderRadius="md"
        height={`${listHeight}px`}
        overflowY="auto"
        data-e2e={`${dataE2ePrefix}-list`}
      >
        {isLoading ? (
          <Center height="100%">
            <Spinner size="sm" color="button.primary" />
          </Center>
        ) : error ? (
          <Center height="100%" px={3}>
            <Text fontSize="sm" color="text.danger">
              {error instanceof Error
                ? error.message
                : 'Failed to load students'}
            </Text>
          </Center>
        ) : candidates.length === 0 ? (
          <Center height="100%">
            {isFetching ? (
              <Spinner size="sm" color="button.primary" />
            ) : (
              <Text fontSize="sm" color="text.tertiary">
                {search
                  ? 'No matching students found'
                  : 'No students available to add'}
              </Text>
            )}
          </Center>
        ) : (
          <VStack align="stretch" gap={0}>
            {candidates.map((student) => {
              const fullName = `${student.first_name ?? ''} ${
                student.last_name ?? ''
              }`.trim() || student.external_reference_id;
              const isSelected = selected?.rosterPersonId === student.id;
              return (
                <CardButton
                  key={student.id}
                  variant="row"
                  selected={isSelected}
                  onClick={() =>
                    onSelect({
                      rosterPersonId: student.id,
                      publicId: student.public_id,
                      name: fullName,
                      grade: student.grade,
                      externalReferenceId: student.external_reference_id,
                    })
                  }
                  description={
                    student.grade
                      ? `Grade ${student.grade} · ${student.external_reference_id}`
                      : student.external_reference_id
                  }
                  data-e2e={`${dataE2ePrefix}-row-${student.id}`}
                >
                  {fullName}
                </CardButton>
              );
            })}
          </VStack>
        )}
      </Box>

      {selected ? (
        <Text
          fontSize="xs"
          color="text.secondary"
          data-e2e={`${dataE2ePrefix}-selected-summary`}
        >
          Selected: {selected.name}
        </Text>
      ) : null}
    </VStack>
  );
}

export default StudentPicker;
